import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export interface SeedBatch {
  id: string;
  customerId: string;
  customerName?: string;
  batchNumber?: string;
  receivedDate: string;
  variety: string;
  numberOfPacks: number;
  quantityPerPack: number;
  quantityReceived: number;
  quantityReserved?: number;
  quantitySown?: number;
  quantityReturned?: number;
  quantityDiscarded?: number;
  quantityAvailable?: number;
  unit: string;
  qualityNotes?: string;
  greenhouseId?: string;
  storageLocation?: string;
  notes?: string;
  createdAt: string;
}

export interface SeedBatchDto {
  id: string;
  customerId?: string;
  customerName?: string;
  customerNameSnapshot?: string;
  ownerName?: string;
  batchNumber: string;
  receivedDate: string;
  orderId?: string;
  orderNumber?: string;
  cropTypeId: string;
  cropTypeName: string;
  variety?: string;
  numberOfPacks: number;
  quantityPerPack: number;
  quantityReceived: number;
  quantityReserved?: number;
  quantitySown?: number;
  quantityReturned?: number;
  quantityDiscarded?: number;
  quantityAvailable: number;
  /** Unit of measure label (e.g. "Kilograms", "Packets"). */
  unit: string;
  /**
   * Receipt approval status. Replaces the previous `qualityApproved` boolean —
   * Store Manager approval covers both data-entry correctness and seed quality.
   * Pending = receipt not yet approved (batch not selectable for sowing).
   * Backend sends numeric enum: 0=Draft, 1=PendingApproval, 2=Approved, 3=Rejected
   */
  approvalStatus: number | "Draft" | "PendingApproval" | "Approved" | "Rejected";
  qualityNotes?: string;
  storageLocation?: string;
  lotNumber?: string;
  packagingDate?: string;
  expiryDate?: string;
  notes?: string;
  createdAt: string;
}

export interface CreateSeedBatchRequest {
  ownershipType: "Customer";
  customerId: string;
  receivedDate: string;
  variety: string;
  numberOfPacks: number;
  quantityPerPack: number;
  unit: number;
  cropTypeId: string;
  qualityNotes?: string;
  storageLocation?: string;
  seedSource: string;
  /** Lot/batch number printed on the customer's seed packet (required). */
  lotNumber: string;
  /** Packaging date printed on the customer's seed packet (required, ISO string). */
  packagingDate: string;
  notes?: string;
}

export const seedBatchesApi = {
  createSeedBatch: async (
    payload: CreateSeedBatchRequest
  ): Promise<SeedBatch> => {
    const { data } = await apiClient.post<ApiResponse<SeedBatch>>(
      "/SeedBatches",
      payload
    );
    return data.data;
  },

  getSeedBatchById: async (id: string): Promise<SeedBatch> => {
    const { data } = await apiClient.get<ApiResponse<SeedBatch>>(
      `/SeedBatches/${id}`
    );
    return data.data;
  },

  getSeedBatchByNumber: async (batchNumber: string): Promise<SeedBatch> => {
    const { data } = await apiClient.get<ApiResponse<SeedBatch>>(
      `/SeedBatches/batch-number/${encodeURIComponent(batchNumber)}`
    );
    return data.data;
  },

  getByOrder: async (orderId: string): Promise<SeedBatchDto[]> => {
    const { data } = await apiClient.get<ApiResponse<SeedBatchDto[]>>(
      `/SeedBatches/order/${orderId}`
    );
    return data.data ?? [];
  },

  getByCustomer: async (customerId: string): Promise<SeedBatchDto[]> => {
    const { data } = await apiClient.get<ApiResponse<SeedBatchDto[]>>(
      `/SeedBatches/customer/${customerId}`
    );
    return data.data ?? [];
  },
};
